import type { OssArchiveRejectError } from "./oss-archive-error";
import { isOssArchiveErrorCode, normalizeOssArchiveError } from "./oss-archive-error";

export interface OssArchiveConfigSnapshot {
	integration?: { enabled?: boolean };
	readGate?: { enabled?: boolean; dryRun?: boolean; configCacheSec?: number };
	restore?: { pollIntervalSec?: number };
	uiCopy?: {
		estimatedWaitText?: string;
		archiveAgeYearsText?: string;
		openApiDegradeMessage?: string;
		dialogVersion?: string;
		defaultLocale?: string;
		locales?: Record<string, import("./oss-archive-copy-defaults").OssArchiveLocaleMessages>;
	};
	debug?: { defaultBucket?: string };
}

let integrationEnabled = false;
let configSnapshot: OssArchiveConfigSnapshot | null = null;
let configLoadedAt = 0;
let configLoader: (() => Promise<OssArchiveConfigSnapshot>) | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let canvasOverlayCount = 0;

type ArchiveErrorListener = (error: OssArchiveRejectError) => void;
const listeners = new Set<ArchiveErrorListener>();

export function registerOssArchiveConfigLoader(loader: () => Promise<OssArchiveConfigSnapshot>) {
	configLoader = loader;
	configLoadedAt = 0;
}

export function registerOssArchiveCanvasOverlay(): () => void {
	canvasOverlayCount += 1;
	let released = false;
	return () => {
		if (released) {
			return;
		}
		released = true;
		canvasOverlayCount = Math.max(0, canvasOverlayCount - 1);
	};
}

export function isOssArchiveCanvasOverlayActive(): boolean {
	return canvasOverlayCount > 0;
}

/** 用 oem/info 等启动引导数据立即水合，不等待 getConfig 轮询。 */
export function hydrateOssArchiveConfig(snapshot?: OssArchiveConfigSnapshot | null): void {
	if (!snapshot || typeof snapshot !== "object") {
		return;
	}
	configSnapshot = snapshot;
	integrationEnabled = Boolean(snapshot.integration?.enabled);
	configLoadedAt = Date.now();
}

export function subscribeOssArchiveError(listener: ArchiveErrorListener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function isOssArchiveIntegrationEnabled(): boolean {
	return integrationEnabled;
}

export function getOssArchiveConfigSnapshot(): OssArchiveConfigSnapshot | null {
	return configSnapshot;
}

export async function refreshOssArchiveIntegrationConfig(force = false): Promise<void> {
	if (!configLoader) {
		return;
	}
	const cacheSec = configSnapshot?.readGate?.configCacheSec || 10;
	if (!force && configLoadedAt && Date.now() - configLoadedAt < cacheSec * 1000) {
		return;
	}
	try {
		const cfg = await configLoader();
		applyOssArchiveConfigSnapshot(cfg);
	} catch {
		// 拉取失败：不写 configLoadedAt，保持尚未加载 → 仍处理 error_code
	}
}

export async function startOssArchiveIntegrationPolling(intervalMs = 15000): Promise<void> {
	if (!refreshTimer) {
		refreshTimer = setInterval(() => {
			void refreshOssArchiveIntegrationConfig(true);
		}, intervalMs);
	}
	await refreshOssArchiveIntegrationConfig(true);
}

function applyOssArchiveConfigSnapshot(cfg?: OssArchiveConfigSnapshot | null): void {
	const explicit = cfg?.integration?.enabled;
	if (typeof explicit !== "boolean") {
		// 空对象 / 缺字段：不能当成关闸
		return;
	}
	configSnapshot = cfg || null;
	integrationEnabled = explicit;
	configLoadedAt = Date.now();
}

function isArchiveHandlingDisabled(): boolean {
	return Boolean(configLoadedAt) && !integrationEnabled;
}

export function dispatchOssArchiveError(error: unknown): OssArchiveRejectError | null {
	const normalized = normalizeOssArchiveError(error);
	if (!normalized || isArchiveHandlingDisabled()) {
		return null;
	}
	listeners.forEach((listener) => listener(normalized));
	return normalized;
}

export function shouldHandleOssArchiveResponse(data?: Record<string, unknown> | null): boolean {
	if (!data || isArchiveHandlingDisabled()) {
		return false;
	}
	return isOssArchiveErrorCode(String(data.error_code || ""));
}
